@if (pageLabels$ | async; as pageLabels) {
  <main class="container">
    <div class="header">
      @if (isPreferredPaymentMethod) {
        <h2>{{ pageLabels.preferredCardPaymentTitleText }}</h2>
      } @else {
        <h2>{{ pageLabels.cardPaymentTitle }}</h2>
      }
    </div>
    <div class="form-wrapper">
      <app-notification [pageName]="pageName"></app-notification>
      @if (cardExpired && showCardExpiredMessage) {
        <p id="cardExpired" class="gs-post-notification-error">{{ pageLabels.creditCardExpiredText }}</p>
      }
      <mat-dialog-content>
        <!-- Card Number -->
        @if (isPreferredPaymentMethod) {
          <p id="paymethod-title" class="payMethod-title">{{ pageLabels.preferredPaymentMethodTitleText }}</p>
          <p id="paymethod-description" class="payMethod-description">{{ preferredPayMethodDescription }}</p>
        }
        <form [formGroup]="creditCardForm" class="card-form" (ngSubmit)="onSubmit()">
          @if (isPreferredPaymentMethod) {
            <p id="preferred-card" class="preferred-card-title">{{ pageLabels.preferredCardNumberTitleText }}</p>
            <input
              id="payment-lastFour-input"
              readonly
              matInput
              aria-labelledby="preferred-card"
              class="payMethod-last-four"
              value="**********{{ selectedPaymethod?.LastFour }}"
            />
          } @else {
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>{{ pageLabels.cardNumberLabel }}</mat-label>
              <input
                #cardNumber
                matInput
                type="text"
                formControlName="cardNumber"
                placeholder=""
                id="card-number"
                data-private
                maxlength="19"
                (blur)="checkForDuplicate()"
                aria-labelledby="cardNumberReq cardNumberInvalid cardNumberTypeError cardNumberLuhnError duplicatePayMethodError"
                [mask]="cardNumberMask"
                (input)="checkMask(cardNumber.value)"
              />
              @if (creditCardForm.get("cardNumber")?.hasError("required")) {
                <mat-error id="cardNumberReq">{{ pageLabels.cardNumberErrorText }}</mat-error>
              } @else if (creditCardForm.get("cardNumber")?.hasError("invalidCardNumber")) {
                <mat-error id="cardNumberInvalid">{{ pageLabels.cardNumberErrorText2 }}</mat-error>
              } @else if (creditCardForm.get("cardNumber")?.hasError("invalidCardType")) {
                <mat-error id="cardNumberTypeError">{{ pageLabels.cardNumberErrorText3 }}</mat-error>
              } @else if (creditCardForm.get("cardNumber")?.hasError("invalidLuhnCheck")) {
                <mat-error id="cardNumberLuhnError">{{ pageLabels.cardNumberErrorText }}</mat-error>
              }
            </mat-form-field>
            @if (!hasError) {
              <p class="error-bottom-margin"></p>
            }
          }
          <!-- Zip Code -->
          @if (!isPreferredPaymentMethod) {
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>{{ pageLabels.zipCodeLabel }}</mat-label>
              <input matInput type="text" formControlName="zipCode" placeholder="" id="zip-code" maxlength="5" />
              @if (creditCardForm.get("zipCode")?.hasError("required")) {
                <mat-error id="zipCodeReqError">{{ pageLabels.zipCodeErrorText }}</mat-error>
              } @else if (creditCardForm.get("zipCode")?.hasError("invalidZipCode")) {
                <mat-error id="zipCodeInvalidError">{{ pageLabels.zipCodeErrorText }}</mat-error>
              }
            </mat-form-field>
          }
          <div class="row">
            <div class="column security-code">
              <!-- Security Code -->
              <mat-form-field appearance="outline" class="remove-margin-bottom" subscriptSizing="dynamic">
                <mat-label>{{ pageLabels.securityCodeLabel }}</mat-label>
                <input
                  matInput
                  type="text"
                  formControlName="securityCode"
                  placeholder=""
                  id="security-code"
                  data-private
                  maxlength="4"
                />
                @if (creditCardForm.get("securityCode")?.hasError("required")) {
                  <mat-error id="securityCodeReqError">{{ pageLabels.securityCodeErrorText }}</mat-error>
                } @else if (creditCardForm.get("securityCode")?.hasError("invalidSecurityCode")) {
                  <mat-error id="securityCodeInvalidError">{{ pageLabels.securityCodeErrorText }}</mat-error>
                }
              </mat-form-field>
              <button
                id="findSecurityCode"
                class="find-securityCode button-link"
                (click)="toggleSecurityCodeImages()"
                aria-expanded="false"
                type="button"
              >
                {{ pageLabels.findSecurityCodeLabel }}
              </button>
              <!-- Security Code Images -->
              @if (showSecurityCodeImages) {
                <div class="security-code-images">
                  @if (isVisaOrMastercardOrDiscover) {
                    <img
                      src="assets/images/Visa_MC_Discover_Solo.png"
                      alt="Security Code - Visa/MasterCard/Discover on the back"
                      class="card-image"
                    />
                  }
                  @if (isAmex) {
                    <img
                      src="assets/images/American_Express_Solo_front.png"
                      alt="Security Code - American Express on the front"
                      class="card-image"
                    />
                  }
                  @if (isUnknownCardType) {
                    <img
                      src="assets/images/Visa_MC_Discover.png"
                      alt="Security Code - Visa/MasterCard/Discover on the back"
                    />
                  }
                  @if (isUnknownCardType) {
                    <img
                      src="assets/images/American_Express_front.png"
                      alt="Security Code - American Express on the front"
                    />
                  }
                </div>
              }
            </div>
            <div class="column expiration-date">
              <!-- Expiration Date -->
              @if (!isPreferredPaymentMethod || cardExpired) {
                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                  <mat-label>{{ pageLabels.expirationDateLabel }}</mat-label>
                  <input
                    matInput
                    type="text"
                    formControlName="expirationDate"
                    placeholder="MM/YY"
                    id="expiration-date"
                    (blur)="onExpirationDateBlur($event)"
                    [dropSpecialCharacters]="false"
                    mask="00/00"
                  />
                  @if (creditCardForm.get("expirationDate")?.hasError("required")) {
                    <mat-error id="expirationDateReqError">{{ pageLabels.expirationDateErrorText }}</mat-error>
                  } @else if (creditCardForm.get("expirationDate")?.hasError("invalidExpirationDate")) {
                    <mat-error id="expirationDateInvalidError">{{
                      pageLabels.invalidExpirationDateErrorText
                    }}</mat-error>
                  }
                </mat-form-field>
              }
            </div>
          </div>
          @if (showPreferencesSection) {
            <p href="#" id="preferences-text">{{ pageLabels.preferencesLabel }}</p>
            <div class="preferences">
              <div class="row">
                @if (isParentPayment) {
                  <div class="column">
                    <mat-checkbox formControlName="saveWallet" (change)="saveWalletOnChange($event)">{{
                      pageLabels.savetowalletLabel
                    }}</mat-checkbox>
                  </div>
                }
                @if (isWalletChecked) {
                  <div class="column">
                    <mat-checkbox formControlName="setDefault">{{ pageLabels.setasefaultLabel }}</mat-checkbox>
                  </div>
                }
              </div>
            </div>
            <!-- Nickname -->
            @if (isWalletChecked) {
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>{{ pageLabels.nicknameLabel }}</mat-label>
                <input
                  matInput
                  type="text"
                  formControlName="nickname"
                  placeholder=""
                  id="nickname"
                  maxlength="45"
                  (blur)="checkForDuplicateNickName()"
                  aria-labelledby="nickNameReqError nickNameInvalidError nickNameInvalidCharError nickNameDuplicateError"
                />
                @if (creditCardForm.get("nickname")?.hasError("required")) {
                  <mat-error id="nickNameReqError">{{ pageLabels.nicknameErrorText }}</mat-error>
                } @else if (creditCardForm.get("nickname")?.hasError("invalidNickname")) {
                  <mat-error id="nickNameInvalidError">{{ pageLabels.nicknameErrorText }}</mat-error>
                } @else if (creditCardForm.get("nickname")?.hasError("invalidCharacters")) {
                  <mat-error id="nickNameInvalidCharError">{{ pageLabels.nicknameErrorText }}</mat-error>
                } @else if (creditCardForm.get("nickname")?.hasError("duplicateNickName")) {
                  <mat-error id="nickNameDuplicateError">{{ pageLabels.duplicateNickNameErrorText }}</mat-error>
                }
              </mat-form-field>
            }
          }
          <!-- Buttons Wrapper -->
          <div class="buttons-wrapper">
            <!-- Cancel Button -->
            <button
              type="button"
              mat-flat-button
              class="gs-form-button-primary cancel-button"
              (click)="onCancelClick()"
              color="primary"
            >
              {{ pageLabels.cancelButtonText }}
            </button>
            <!-- Save Button -->
            <button mat-flat-button class="gs-form-button-primary" color="primary">
              {{ pageLabels.saveButtonText }}
            </button>
          </div>
        </form>
      </mat-dialog-content>
    </div>
  </main>
}
